import { type ComponentType, type LazyExoticComponent, lazy } from "react";

/**
 * POC-653: React.lazy reads `module.default` without a guard. After a deploy,
 * a stale or mangled chunk can fulfill as `undefined` / without a default
 * export, which surfaces in Sentry as:
 *   TypeError: Cannot read properties of undefined (reading 'default')
 * Culprit: /checkout.
 *
 * Shares the same one-shot sessionStorage key as the vite:preloadError
 * handler in index.tsx so a single recovery reload covers both failure modes
 * without looping.
 *
 * The guard is cleared only once a lazy module actually resolves — never on
 * `window.load`. Clearing on load re-armed the reload before the failing
 * chunk was even requested, so a chunk that kept failing (a dev transform
 * error, a stale bundle) reloaded the dashboard forever: PageLoader, refetch
 * /users/me, reload, repeat (POC-817 local Payouts loop).
 */
export const LAZY_CHUNK_RELOAD_KEY = "vite-preload-reloaded";

type ModuleWithDefault<T> = { default: T };

function canReloadOnce(): boolean {
	try {
		if (sessionStorage.getItem(LAZY_CHUNK_RELOAD_KEY) === "1") {
			return false;
		}
		sessionStorage.setItem(LAZY_CHUNK_RELOAD_KEY, "1");
		return true;
	} catch {
		return false;
	}
}

/** Clear the reload guard after a lazy chunk resolved successfully. */
export function clearLazyChunkReloadFlag(): void {
	try {
		sessionStorage.removeItem(LAZY_CHUNK_RELOAD_KEY);
	} catch {
		// sessionStorage unavailable (sandboxed iframe) — ignore.
	}
}

/**
 * Schedule a one-shot full reload for a broken lazy chunk. Returns true when
 * a reload was armed (caller should hang the import promise); false when we
 * already reloaded once this session and must rethrow.
 */
export function recoverBrokenLazyChunk(): boolean {
	if (typeof window === "undefined") return false;
	if (!canReloadOnce()) return false;
	window.location.reload();
	return true;
}

function isValidDefaultExport<T>(mod: unknown): mod is ModuleWithDefault<T> {
	return (
		!!mod &&
		typeof mod === "object" &&
		"default" in mod &&
		(mod as ModuleWithDefault<T>).default != null
	);
}

/**
 * `lazyWithRecovery` for pages exposed as a named export. Reading
 * `m.SomeExport` off a stale chunk that fulfilled as `undefined` throws
 * "Cannot read properties of undefined (reading 'SomeExport')" inside the
 * import().then() callback, before React.lazy ever sees a module — so the
 * named-export pick has to happen inside the recovery wrapper.
 */
export function lazyNamedWithRecovery<
	// biome-ignore lint/suspicious/noExplicitAny: mirrors React.lazy
	T extends ComponentType<any>,
	K extends string,
>(
	importer: () => Promise<Record<K, T>>,
	exportName: K,
): LazyExoticComponent<T> {
	return lazyWithRecovery(async () => {
		const mod = await importer();
		return { default: (mod as Record<K, T> | undefined)?.[exportName] as T };
	});
}

/**
 * Sentry FRONTEND-3W / FRONTEND-6B: a stale or mangled chunk can fulfill as a
 * module with named exports only (no `default`). React.lazy then throws
 * "no default export" for /checkout and /profile. Fall back to the most
 * plausible component export instead of crashing — the one-shot reload guard
 * below still covers the fully-broken case.
 */
function namedExportFallback<T>(mod: unknown): ModuleWithDefault<T> | null {
	if (!mod || typeof mod !== "object") return null;
	const record = mod as Record<string, unknown>;
	// Prefer an explicitly-named page component, else the first export.
	const candidate =
		record.Component ?? record.Page ?? Object.values(record)[0];
	if (candidate == null) return null;
	console.warn(
		"[lazyWithRecovery] module resolved without a default export; using named-export fallback.",
	);
	return { default: candidate as T };
}

/**
 * Drop-in for `React.lazy` that validates the default export and recovers
 * from stale/broken chunks with a single clean reload.
 */
export function lazyWithRecovery<
	// Match React.lazy's ComponentType bound (props are caller-defined).
	// biome-ignore lint/suspicious/noExplicitAny: mirrors React.lazy
	T extends ComponentType<any>,
>(factory: () => Promise<ModuleWithDefault<T>>): LazyExoticComponent<T> {
	return lazy(async () => {
		try {
			const mod = await factory();
			if (isValidDefaultExport<T>(mod)) {
				// A good chunk proves the bundle is healthy again; re-arm the
				// one-shot recovery for the next deploy-time stale chunk.
				clearLazyChunkReloadFlag();
				return mod;
			}
			const fallback = namedExportFallback<T>(mod);
			if (fallback) {
				clearLazyChunkReloadFlag();
				return fallback;
			}
			if (recoverBrokenLazyChunk()) {
				// Hang until the reload completes so React never reads `.default`.
				return new Promise<ModuleWithDefault<T>>(() => {});
			}
			throw new TypeError("Lazy module resolved without a default export");
		} catch (error) {
			if (recoverBrokenLazyChunk()) {
				return new Promise<ModuleWithDefault<T>>(() => {});
			}
			throw error;
		}
	});
}
